import * as React from 'react'

import Loading from 'airship/patterns/Loading'
import Feedback from 'airship/patterns/feedback'
import Modal from 'airship/patterns/modal'
import TextareaInput from 'airship/components/Validator/TextareaInput'
import Button from 'airship/patterns/button'
import Select from 'airship/patterns/select'
import RadioSelect from 'airship/patterns/RadioSelect'
import {useSelector} from 'react-redux'
import getContext from 'airship/modules/page-context'
import execute from 'airship/modules/grecaptcha'
import Script from 'airship/components/script'

import detailsSlice from './detailsSlice'
import {
  selectProduct,
  selectIssueDescription,
  selectUrgency,
  selectLoading,
  selectErrorMessage,
} from './selectors'
import {createTicketThunk} from './thunks'
import store, {useInProductSupportDispatch} from './store'
import {resolveContext} from './types'

import {productsSelect} from './products'
import Details from './details'
import EditDetails from './editDetails'
import ThankYou from './thankYou'
import './InProductSupport.css'

interface Props {
  isOpen: boolean
  onClose: () => void
}

export interface DetailsData {
  appName: string
  url: string
  appKey: string
}

const ContactSupportModal: React.FC<Props> = ({isOpen, onClose}) => {
  const [showEditDetails, setShowEditDetails] = React.useState<boolean>(false)
  const [showThankYou, setShowThankYou] = React.useState<boolean>(false)

  const {recaptcha} = getContext(resolveContext).context_processors

  const product = useSelector(selectProduct)
  const issueDescription = useSelector(selectIssueDescription)
  const urgency = useSelector(selectUrgency)
  const loading = useSelector(selectLoading)
  const errorMessage = useSelector(selectErrorMessage)

  const dispatch = useInProductSupportDispatch()
  const state = store.getState().details

  const urgencySelectorItems = [
    {id: 'low', label: 'Low'},
    {id: 'medium', label: 'Medium'},
    {id: 'highest', label: 'High'},
    {id: 'urgent', label: 'Critical'},
  ] as const

  const handleSend = async (): Promise<void> => {
    const details = {...state.details}
    dispatch(detailsSlice.actions.clearErrorMessage())

    if (!product) {
      dispatch(
        detailsSlice.actions.updateErrorMessage('Please select a product.')
      )
      return
    }

    if (!issueDescription) {
      dispatch(
        detailsSlice.actions.updateErrorMessage('Please describe your issue.')
      )
      return
    }

    if (!details.appName) details.appName = 'Not provided'
    if (!details.appKey) details.appKey = 'Not provided'

    setShowEditDetails(false)

    if (recaptcha.require_captcha) {
      const token = await execute(recaptcha.recaptcha_site_key)
      details.recaptchaToken = token
    }

    const result = await dispatch(createTicketThunk(details))
    if (createTicketThunk.fulfilled.match(result)) {
      setShowThankYou(true)
    }
  }

  const updateProduct = React.useCallback(
    (newProduct) => {
      dispatch(detailsSlice.actions.updateProduct(newProduct))
    },
    [dispatch]
  )

  const updateIssueDescription = React.useCallback(
    (newDescription) => {
      dispatch(detailsSlice.actions.updateIssueDescription(newDescription))
    },
    [dispatch]
  )

  const updateUrgency = React.useCallback(
    (newUrgency) => {
      dispatch(detailsSlice.actions.updateUrgency(newUrgency))
    },
    [dispatch]
  )

  const handleClose = (): void => {
    setShowThankYou(false)
    setShowEditDetails(false)
    dispatch(detailsSlice.actions.clearFields())
    onClose()
  }

  return (
    <>
      {recaptcha.require_captcha && (
        <Script
          url={`https://www.google.com/recaptcha/api.js?render=${recaptcha.recaptcha_site_key}`}
        ></Script>
      )}
      <Modal size="md" isOpen={isOpen} onClose={onClose}>
        {loading ? (
          <div className="u-lg-pad-6">
            <Loading centerInParent={true} />
          </div>
        ) : showThankYou ? (
          <ThankYou handleClose={handleClose} />
        ) : (
          <div>
            <h1 className="u-space-bottom-1">Contact Support</h1>
            {showEditDetails ? (
              <EditDetails setShowEditDetails={setShowEditDetails} />
            ) : (
              <Details setShowEditDetails={setShowEditDetails} />
            )}
            <h1 className="u-space-bottom-05">Where is your issue?</h1>

            <Select
              displayClasses="u-space-bottom-05"
              selected={product}
              items={productsSelect}
              onSelect={updateProduct}
              defaultItemText="Select product or feature"
            />

            <h1 className="u-space-bottom-05">Describe your issue</h1>
            <TextareaInput
              displayClasses="u-space-bottom-2"
              id="issue"
              size="md"
              value={issueDescription}
              onChange={updateIssueDescription}
            />
            <h1 className="u-space-bottom-05">Urgency</h1>
            <RadioSelect
              displayClasses="u-text-medium u-space-bottom-1"
              items={urgencySelectorItems}
              selected={urgency}
              onSelect={(id) => updateUrgency(id)}
            />
            {errorMessage && (
              <Feedback displayClasses="u-space-bottom-1" type="error">
                {errorMessage}
              </Feedback>
            )}
            <div className="u-flex u-flex-justify-end">
              <Button onClick={handleClose} displayClasses="u-space-right-05">
                Cancel
              </Button>
              <Button
                onClick={handleSend}
                style="navy"
                displayClasses="u-border-none"
              >
                Contact Support
              </Button>
            </div>
          </div>
        )}
        {isOpen && (
          <div className="recaptcha-footer-message">
            <p className="u-text-center u-grey-20 u-text-shrink-2">
              This site is protected by reCAPTCHA and the{' '}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="u-grey-20 u-text-underline"
              >
                Google Privacy Policy
              </a>{' '}
              and{' '}
              <a
                href="https://policies.google.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="u-grey-20 u-text-underline"
              >
                Terms of Service
              </a>{' '}
              apply.
            </p>
          </div>
        )}
      </Modal>
    </>
  )
}

export default ContactSupportModal
